class TmsQuickView extends HTMLElement {
  static FETCH_TIMEOUT_MS = 10000;

  connectedCallback() {
    this.panel = this.querySelector('.tms-quick-view__panel');
    this.content = this.querySelector('[data-tms-quick-view-content]');
    this.loading = this.querySelector('[data-tms-quick-view-loading]');
    this.openedBy = null;
    this.previousBodyOverflow = '';

    document.addEventListener('click', this.onDocumentClick.bind(this), true);
    document.addEventListener('keydown', this.onKeydown.bind(this));
  }

  onDocumentClick(event) {
    // Capture phase: check if a quick-view trigger sits at the click point
    // even when another element (e.g. the full-card link's ::after overlay)
    // is sitting on top of it in the stacking order.
    let trigger = event.target.closest('[data-tms-quick-view]');

    // Only look beneath the click point when the click landed on a product card: anything
    // layered over a card (drawer, this modal, mobile menu) must keep its own clicks.
    const card = event.target.closest('.card-wrapper');
    if (!trigger && card && typeof document.elementsFromPoint === 'function') {
      const stack = document.elementsFromPoint(event.clientX, event.clientY);
      for (const el of stack) {
        if (el.matches && el.matches('[data-tms-quick-view]') && card.contains(el)) {
          trigger = el;
          break;
        }
        const ancestor = el.closest && el.closest('[data-tms-quick-view]');
        if (ancestor && card.contains(ancestor)) {
          trigger = ancestor;
          break;
        }
      }
    }

    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      const url = trigger.dataset.productUrl;
      if (url) this.open(url, trigger);
      return;
    }

    if (event.target.closest('[data-tms-quick-view-close]')) {
      this.close();
    }
  }

  onKeydown(event) {
    if (this.hasAttribute('hidden')) return;
    if (event.key === 'Escape') {
      this.close();
    } else if (event.key === 'Tab') {
      this.trapTab(event);
    }
  }

  // Keep keyboard focus inside the dialog. Focusables are recomputed on every
  // Tab because the fetched product content upgrades its custom elements
  // asynchronously, which a first/last snapshot taken at open time would miss.
  trapTab(event) {
    const focusables = Array.from(
      this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, summary, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.tagName === 'IFRAME' || el.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const outside = !this.panel.contains(active);
    if (event.shiftKey && (active === first || active === this.panel || outside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || outside)) {
      event.preventDefault();
      first.focus();
    }
  }

  async open(url, trigger) {
    this.openedBy = trigger;
    // Show an in-button spinner on the card control that opened the modal.
    if (trigger) {
      trigger.classList.add('loading');
      trigger.querySelector('.loading__spinner')?.classList.remove('hidden');
    }
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    this.content.replaceChildren();
    this.classList.add('is-loading');
    this.removeAttribute('hidden');
    requestAnimationFrame(() => this.classList.add('is-open'));
    this.panel.focus({ preventScroll: true });

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TmsQuickView.FETCH_TIMEOUT_MS);

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const productInfo = doc.querySelector('product-info');
      if (!productInfo) throw new Error('No product-info element in response');

      this.preprocess(productInfo);
      this.content.appendChild(productInfo);
      this.upgradeCustomElements(productInfo);

      if (window.Shopify && window.Shopify.PaymentButton) {
        window.Shopify.PaymentButton.init();
      }
    } catch (error) {
      this.renderError();
    } finally {
      clearTimeout(timer);
      this.classList.remove('is-loading');
      if (trigger) {
        trigger.classList.remove('loading');
        trigger.querySelector('.loading__spinner')?.classList.add('hidden');
      }
    }
  }

  upgradeCustomElements(root) {
    if (typeof customElements === 'undefined' || typeof customElements.upgrade !== 'function') return;
    customElements.upgrade(root);
    root.querySelectorAll('*').forEach((el) => {
      if (el.tagName && el.tagName.includes('-')) {
        customElements.upgrade(el);
      }
    });
  }

  renderError() {
    const p = document.createElement('p');
    p.className = 'tms-quick-view__error';
    p.textContent = this.errorMessage();
    this.content.replaceChildren(p);
  }

  preprocess(productInfo) {
    productInfo.setAttribute('data-update-url', 'false');

    const pickup = productInfo.querySelector('pickup-availability');
    if (pickup) pickup.remove();

    productInfo.querySelectorAll('product-modal, modal-dialog').forEach((el) => el.remove());

    // Disambiguate IDs by replacing only the section-id substring inside
    // every attribute. Preserves semantic prefixes like "GalleryThumbnails-"
    // so attribute selectors used by media-gallery / product-info still work.
    const sectionId = productInfo.dataset.section;
    if (sectionId) {
      const newSectionId = `qv-${sectionId}`;
      const walker = document.createTreeWalker(productInfo, NodeFilter.SHOW_ELEMENT);
      let node = walker.currentNode;
      while (node) {
        for (const attr of Array.from(node.attributes || [])) {
          if (attr.value.includes(sectionId)) {
            node.setAttribute(attr.name, attr.value.split(sectionId).join(newSectionId));
          }
        }
        node = walker.nextNode();
      }
      productInfo.dataset.originalSection = sectionId;
      productInfo.dataset.section = newSectionId;
    }
  }

  close() {
    if (this.hasAttribute('hidden')) return;
    this.classList.remove('is-open');
    document.body.style.overflow = this.previousBodyOverflow;

    const finalize = () => {
      this.setAttribute('hidden', '');
      this.content.replaceChildren();
      if (this.openedBy) {
        try {
          this.openedBy.focus({ preventScroll: true });
        } catch (e) {
          // ignore
        }
        this.openedBy = null;
      }
    };

    this.panel.addEventListener('transitionend', finalize, { once: true });
    setTimeout(finalize, 350);
  }

  errorMessage() {
    const node = document.querySelector('[data-tms-quick-view-error-message]');
    return node ? node.textContent : 'Could not load product. Please try again.';
  }
}

if (!customElements.get('tms-quick-view')) {
  customElements.define('tms-quick-view', TmsQuickView);
}
